import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { ApiUrlService } from '@pryspace/shared/environments';
import {
  MerchDiscoveryProduct,
  MerchDiscoverySession,
  MerchDiscoverySessionWrite,
  MerchBrand,
  MerchBrandWrite,
  MerchEvaluation,
  MerchEvaluationPatch,
  MerchProbeRequest,
  MerchProbeResult,
  MerchProduct,
  MerchProductIdea,
  MerchProductIdeaFile,
  MerchProductIdeaWrite,
  MerchProductSource,
  MerchProductSourcePatch,
  MerchProviderCatalogPage,
  MerchProviderCatalogSyncResult,
  MerchProviderConnection,
  MerchProviderConnectionWrite,
  MerchProviderDefinition,
  MerchProviderSyncResult,
  MerchProviderTemplate,
  MerchProviderTemplateSyncResult,
  MerchSample,
  MerchSamplePatch,
  MerchStoreListing,
  MerchStoreListingWrite,
  Paginated,
  unwrapList,
} from './models';

/** Typed client for the tenant-scoped Merch Master Controller API. */
@Injectable({ providedIn: 'root' })
export class MerchService {
  private http = inject(HttpClient);
  private api = inject(ApiUrlService);

  private base(slug: string): string {
    return `v1/tenants/${encodeURIComponent(slug)}/merch`;
  }

  providerDefinitions(slug: string) {
    return firstValueFrom(this.http.get<MerchProviderDefinition[]>(
      this.api.url(`${this.base(slug)}/provider-definitions/`),
    ));
  }

  probe(slug: string, body: MerchProbeRequest) {
    return firstValueFrom(this.http.post<MerchProbeResult>(
      this.api.url(`${this.base(slug)}/probes/test/`), body,
    ));
  }

  async providers(slug: string): Promise<MerchProviderConnection[]> {
    const response = await firstValueFrom(this.http.get<Paginated<MerchProviderConnection>>(
      this.api.url(`${this.base(slug)}/providers/`),
    ));
    return unwrapList(response);
  }

  provider(slug: string, id: string) {
    return firstValueFrom(this.http.get<MerchProviderConnection>(
      this.api.url(`${this.base(slug)}/providers/${id}/`),
    ));
  }

  createProvider(slug: string, body: MerchProviderConnectionWrite) {
    return firstValueFrom(this.http.post<MerchProviderConnection>(
      this.api.url(`${this.base(slug)}/providers/`), body,
    ));
  }

  updateProvider(slug: string, id: string, body: Partial<MerchProviderConnectionWrite>) {
    return firstValueFrom(this.http.patch<MerchProviderConnection>(
      this.api.url(`${this.base(slug)}/providers/${id}/`), body,
    ));
  }

  testProvider(slug: string, id: string) {
    return firstValueFrom(this.http.post<MerchProbeResult>(
      this.api.url(`${this.base(slug)}/providers/${id}/test/`), {},
    ));
  }

  syncProvider(slug: string, id: string) {
    return firstValueFrom(this.http.post<MerchProviderSyncResult>(
      this.api.url(`${this.base(slug)}/providers/${id}/sync/`), {},
    ));
  }

  syncProviderCatalogCache(slug: string, id: string) {
    return firstValueFrom(this.http.post<MerchProviderCatalogSyncResult>(
      this.api.url(`${this.base(slug)}/providers/${id}/catalog-cache/sync/`), {},
    ));
  }

  syncProviderTemplates(slug: string, connectionId: string) {
    return firstValueFrom(this.http.post<MerchProviderTemplateSyncResult>(
      this.api.url(`${this.base(slug)}/providers/${connectionId}/templates/sync/`), {},
    ));
  }

  async providerTemplates(
    slug: string,
    filters: {
      connectionId?: string;
      provider?: string;
      templateReady?: boolean;
      q?: string;
      ideaId?: string;
    } = {},
  ): Promise<MerchProviderTemplate[]> {
    let params = new HttpParams();
    if (filters.connectionId) params = params.set('connection_id', filters.connectionId);
    if (filters.provider) params = params.set('provider', filters.provider);
    if (filters.templateReady !== undefined) {
      params = params.set('template_ready', String(filters.templateReady));
    }
    if (filters.q) params = params.set('q', filters.q);
    if (filters.ideaId) params = params.set('idea_id', filters.ideaId);
    const response = await firstValueFrom(this.http.get<
      MerchProviderTemplate[] | Paginated<MerchProviderTemplate>
    >(this.api.url(`${this.base(slug)}/provider-templates/`), { params }));
    return unwrapList(response);
  }

  providerTemplate(slug: string, id: string, ideaId?: string) {
    const params = ideaId ? new HttpParams().set('idea_id', ideaId) : undefined;
    return firstValueFrom(this.http.get<MerchProviderTemplate>(
      this.api.url(`${this.base(slug)}/provider-templates/${id}/`),
      { params },
    ));
  }

  providerCatalog(
    slug: string,
    filters: {
      q?: string;
      provider?: string;
      connectionId?: string;
      category?: string;
      productKind?: 'all' | 't_shirt' | 'hoodie' | 'headwear' | 'mousepad' | 'other';
      printKind?: 'all' | 'standard' | 'all_over';
      rated?: boolean;
      promoted?: boolean;
      lifecycle?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ) {
    let params = new HttpParams();
    if (filters.q) params = params.set('q', filters.q);
    if (filters.provider && filters.provider !== 'all') {
      params = params.set('provider', filters.provider);
    }
    if (filters.connectionId) params = params.set('connection_id', filters.connectionId);
    if (filters.category && filters.category !== 'all') {
      params = params.set('category', filters.category);
    }
    if (filters.productKind && filters.productKind !== 'all') {
      params = params.set('product_kind', filters.productKind);
    }
    if (filters.printKind && filters.printKind !== 'all') {
      params = params.set('print_kind', filters.printKind);
    }
    if (filters.rated !== undefined) params = params.set('rated', String(filters.rated));
    if (filters.promoted !== undefined) params = params.set('promoted', String(filters.promoted));
    if (filters.lifecycle) params = params.set('lifecycle', filters.lifecycle);
    if (filters.page) params = params.set('page', filters.page);
    if (filters.pageSize) params = params.set('page_size', filters.pageSize);
    return firstValueFrom(this.http.get<MerchProviderCatalogPage>(
      this.api.url(`${this.base(slug)}/catalog-cache/products/`), { params },
    ));
  }

  async discoverySessions(slug: string): Promise<MerchDiscoverySession[]> {
    const response = await firstValueFrom(this.http.get<Paginated<MerchDiscoverySession>>(
      this.api.url(`${this.base(slug)}/discovery/sessions/`),
    ));
    return unwrapList(response);
  }

  createDiscoverySession(slug: string, body: MerchDiscoverySessionWrite) {
    return firstValueFrom(this.http.post<MerchDiscoverySession>(
      this.api.url(`${this.base(slug)}/discovery/sessions/`), body,
    ));
  }

  discoverySession(slug: string, id: string) {
    return firstValueFrom(this.http.get<MerchDiscoverySession>(
      this.api.url(`${this.base(slug)}/discovery/sessions/${id}/`),
    ));
  }

  updateDiscoverySession(slug: string, id: string, body: Partial<MerchDiscoverySessionWrite>) {
    return firstValueFrom(this.http.patch<MerchDiscoverySession>(
      this.api.url(`${this.base(slug)}/discovery/sessions/${id}/`), body,
    ));
  }

  testDiscoverySession(slug: string, id: string) {
    return firstValueFrom(this.http.post<MerchProbeResult>(
      this.api.url(`${this.base(slug)}/discovery/sessions/${id}/test/`), {},
    ));
  }

  async discover(slug: string, id: string): Promise<MerchDiscoveryProduct[]> {
    const response = await firstValueFrom(this.http.post<
      MerchDiscoveryProduct[] | { results: MerchDiscoveryProduct[] }
    >(this.api.url(`${this.base(slug)}/discovery/sessions/${id}/discover/`), {}));
    return unwrapList(response);
  }

  async discoveryProducts(slug: string, sessionId?: string): Promise<MerchDiscoveryProduct[]> {
    let params = new HttpParams();
    if (sessionId) params = params.set('session_id', sessionId);
    const response = await firstValueFrom(this.http.get<Paginated<MerchDiscoveryProduct>>(
      this.api.url(`${this.base(slug)}/discovery/products/`), { params },
    ));
    return unwrapList(response);
  }

  discoveryProduct(slug: string, id: string) {
    return firstValueFrom(this.http.get<MerchDiscoveryProduct>(
      this.api.url(`${this.base(slug)}/discovery/products/${id}/`),
    ));
  }

  updateDiscoveryProduct(slug: string, id: string, body: Partial<MerchDiscoveryProduct>) {
    return firstValueFrom(this.http.patch<MerchDiscoveryProduct>(
      this.api.url(`${this.base(slug)}/discovery/products/${id}/`), body,
    ));
  }

  promoteDiscoveryProduct(slug: string, id: string, connectionId: string) {
    return firstValueFrom(this.http.post<{ created: boolean; product: MerchProduct }>(
      this.api.url(`${this.base(slug)}/discovery/products/${id}/promote/`),
      { connection_id: connectionId },
    ));
  }

  async products(slug: string, filters: { lifecycle?: string; category?: string } = {}) {
    let params = new HttpParams();
    if (filters.lifecycle) params = params.set('lifecycle', filters.lifecycle);
    if (filters.category) params = params.set('category', filters.category);
    const response = await firstValueFrom(this.http.get<Paginated<MerchProduct>>(
      this.api.url(`${this.base(slug)}/products/`), { params },
    ));
    return unwrapList(response);
  }

  product(slug: string, id: string) {
    return firstValueFrom(this.http.get<MerchProduct>(
      this.api.url(`${this.base(slug)}/products/${id}/`),
    ));
  }

  updateProduct(slug: string, id: string, body: Partial<MerchProduct>) {
    return firstValueFrom(this.http.patch<MerchProduct>(
      this.api.url(`${this.base(slug)}/products/${id}/`), body,
    ));
  }

  evaluation(slug: string, productId: string) {
    return firstValueFrom(this.http.get<MerchEvaluation | null>(
      this.api.url(`${this.base(slug)}/products/${productId}/evaluation/`),
    ));
  }

  updateEvaluation(slug: string, productId: string, body: MerchEvaluationPatch) {
    return firstValueFrom(this.http.patch<MerchEvaluation>(
      this.api.url(`${this.base(slug)}/products/${productId}/evaluation/`), body,
    ));
  }

  deleteEvaluation(slug: string, productId: string) {
    return firstValueFrom(this.http.delete<void>(
      this.api.url(`${this.base(slug)}/products/${productId}/evaluation/`),
    ));
  }

  async brands(slug: string): Promise<MerchBrand[]> {
    const response = await firstValueFrom(this.http.get<Paginated<MerchBrand>>(
      this.api.url(`${this.base(slug)}/brands/`),
    ));
    return unwrapList(response);
  }

  createBrand(slug: string, body: MerchBrandWrite) {
    return firstValueFrom(this.http.post<MerchBrand>(
      this.api.url(`${this.base(slug)}/brands/`), body,
    ));
  }

  updateBrand(slug: string, id: string, body: Partial<MerchBrandWrite>) {
    return firstValueFrom(this.http.patch<MerchBrand>(
      this.api.url(`${this.base(slug)}/brands/${id}/`), body,
    ));
  }

  deleteBrand(slug: string, id: string) {
    return firstValueFrom(this.http.delete<void>(
      this.api.url(`${this.base(slug)}/brands/${id}/`),
    ));
  }

  async ideas(slug: string): Promise<MerchProductIdea[]> {
    const response = await firstValueFrom(this.http.get<Paginated<MerchProductIdea>>(
      this.api.url(`${this.base(slug)}/ideas/`),
    ));
    return unwrapList(response);
  }

  createIdea(slug: string, body: MerchProductIdeaWrite) {
    return firstValueFrom(this.http.post<MerchProductIdea>(
      this.api.url(`${this.base(slug)}/ideas/`), body,
    ));
  }

  updateIdea(slug: string, id: string, body: Partial<MerchProductIdeaWrite>) {
    return firstValueFrom(this.http.patch<MerchProductIdea>(
      this.api.url(`${this.base(slug)}/ideas/${id}/`), body,
    ));
  }

  deleteIdea(slug: string, id: string) {
    return firstValueFrom(this.http.delete<void>(
      this.api.url(`${this.base(slug)}/ideas/${id}/`),
    ));
  }

  uploadIdeaFile(slug: string, ideaId: string, file: File, isMockup = false) {
    const body = new FormData();
    body.append('file', file, file.name);
    body.append('is_mockup', isMockup ? 'true' : 'false');
    return firstValueFrom(this.http.post<MerchProductIdeaFile>(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/files/`), body,
    ));
  }

  deleteIdeaFile(slug: string, ideaId: string, fileId: string) {
    return firstValueFrom(this.http.delete<void>(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/files/${fileId}/`),
    ));
  }

  ideaFileBlob(slug: string, ideaId: string, fileId: string) {
    return firstValueFrom(this.http.get(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/files/${fileId}/content/`),
      { responseType: 'blob' },
    ));
  }

  storeListing(slug: string, ideaId: string) {
    return firstValueFrom(this.http.get<MerchStoreListing | null>(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/store-listing/`),
    ));
  }

  ideaProviderTemplate(slug: string, ideaId: string) {
    return firstValueFrom(this.http.get<MerchProviderTemplate | null>(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/provider-template/`),
    ));
  }

  assignIdeaProviderTemplate(slug: string, ideaId: string, providerTemplateId: string) {
    return firstValueFrom(this.http.put<MerchProviderTemplate>(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/provider-template/`),
      { provider_template_id: providerTemplateId },
    ));
  }

  unassignIdeaProviderTemplate(slug: string, ideaId: string) {
    return firstValueFrom(this.http.delete<void>(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/provider-template/`),
    ));
  }

  upsertStoreListing(slug: string, ideaId: string, body: MerchStoreListingWrite) {
    return firstValueFrom(this.http.put<MerchStoreListing>(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/store-listing/`), body,
    ));
  }

  publishStoreListing(slug: string, ideaId: string) {
    return firstValueFrom(this.http.post<MerchStoreListing>(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/store-listing/publish/`), {},
    ));
  }

  unpublishStoreListing(slug: string, ideaId: string) {
    return firstValueFrom(this.http.delete<MerchStoreListing>(
      this.api.url(`${this.base(slug)}/ideas/${ideaId}/store-listing/publish/`),
    ));
  }

  async samples(slug: string, ideaId?: string): Promise<MerchSample[]> {
    let params = new HttpParams();
    if (ideaId) params = params.set('idea_id', ideaId);
    const response = await firstValueFrom(this.http.get<Paginated<MerchSample>>(
      this.api.url(`${this.base(slug)}/samples/`), { params },
    ));
    return unwrapList(response);
  }

  createSampleBatch(
    slug: string,
    ideaId: string,
    productIds: string[],
    fileIds: string[] = [],
  ) {
    return firstValueFrom(this.http.post<MerchSample[]>(
      this.api.url(`${this.base(slug)}/samples/batch/`),
      { idea_id: ideaId, product_ids: productIds, file_ids: fileIds },
    ));
  }

  updateSample(slug: string, id: string, body: MerchSamplePatch) {
    return firstValueFrom(this.http.patch<MerchSample>(
      this.api.url(`${this.base(slug)}/samples/${id}/`), body,
    ));
  }

  deleteSample(slug: string, id: string) {
    return firstValueFrom(this.http.delete<void>(
      this.api.url(`${this.base(slug)}/samples/${id}/`),
    ));
  }

  approveSample(
    slug: string,
    id: string,
    kind?: 'primary' | 'alternative',
    notes = '',
  ) {
    return firstValueFrom(this.http.post<MerchProductSource>(
      this.api.url(`${this.base(slug)}/samples/${id}/approve/`),
      { ...(kind ? { kind } : {}), notes },
    ));
  }

  revokeSampleApproval(slug: string, id: string) {
    return firstValueFrom(this.http.post<MerchSample>(
      this.api.url(`${this.base(slug)}/samples/${id}/revoke-approval/`), {},
    ));
  }

  async productSources(slug: string, ideaId?: string): Promise<MerchProductSource[]> {
    let params = new HttpParams().set('active', 'true');
    if (ideaId) params = params.set('idea_id', ideaId);
    const response = await firstValueFrom(this.http.get<Paginated<MerchProductSource>>(
      this.api.url(`${this.base(slug)}/product-sources/`), { params },
    ));
    return unwrapList(response);
  }

  updateProductSource(slug: string, id: string, body: MerchProductSourcePatch) {
    return firstValueFrom(this.http.patch<MerchProductSource>(
      this.api.url(`${this.base(slug)}/product-sources/${id}/`), body,
    ));
  }
}
